/**
 * AcenteDeletedAgencies — admin-only archive of soft-deleted (companies)
 * agencies alongside their cascade-archived reservations.
 *
 * Design rationale: When an admin deletes an agency (usually via the cascade
 * dialog), the reservations attached to it aren't lost — they're archived
 * with `deleted_reason='agency_cascade'`. This page is where those archives
 * live. Each row is expandable: click the arrow to drill into the archived
 * reservations for that agency; a "Geri Getir" button restores the agency
 * (and all its reservations) in one shot.
 *
 * Endpoints used:
 *   GET  /agencies/deleted                        — list of soft-deleted agencies
 *   GET  /agencies/{id}/deleted-reservations      — drilldown per agency
 *   POST /agencies/{id}/restore                   — restore agency + its reservations
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import panelApi from "@/lib/panelApi";
import { formatTr } from "@/lib/date";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Building2, ChevronDown, ChevronRight, Undo2, Archive,
  Calendar, Users, BedDouble, Receipt,
} from "lucide-react";

export default function AcenteDeletedAgencies() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  // Expansion state — map of agency_id → { open, loading, reservations }.
  const [expanded, setExpanded] = useState({});
  const [restoringId, setRestoringId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await panelApi.get("/agencies/deleted");
      setRows(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Silinen şirketler listelenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      (r.name || "").toLowerCase().includes(needle) ||
      (r.email || "").toLowerCase().includes(needle) ||
      (r.deleted_by_name || "").toLowerCase().includes(needle)
    );
  }, [rows, q]);

  const toggleRow = async (agency) => {
    setExpanded((prev) => {
      const cur = prev[agency.id];
      // Collapsing — just toggle.
      if (cur?.open) return { ...prev, [agency.id]: { ...cur, open: false } };
      // Expanding — mark as loading immediately.
      return { ...prev, [agency.id]: { open: true, loading: true, reservations: cur?.reservations || [] } };
    });
    // If we haven't loaded reservations yet, fetch them.
    const cur = expanded[agency.id];
    if (!cur || !cur.reservations?.length) {
      try {
        const r = await panelApi.get(`/agencies/${agency.id}/deleted-reservations`);
        setExpanded((prev) => ({
          ...prev,
          [agency.id]: { open: true, loading: false, reservations: r.data || [] },
        }));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Rezervasyonlar yüklenemedi");
        setExpanded((prev) => ({
          ...prev,
          [agency.id]: { open: true, loading: false, reservations: [] },
        }));
      }
    }
  };

  const restore = async (agency) => {
    if (!window.confirm(
      `"${agency.name}" şirketi ve ${agency.reservation_count} rezervasyonu geri getirilecek. Onaylıyor musunuz?`
    )) return;
    setRestoringId(agency.id);
    try {
      const r = await panelApi.post(`/agencies/${agency.id}/restore`);
      toast.success(`${agency.name} geri getirildi — ${r?.data?.restored_reservations ?? 0} rezervasyon yeniden aktif`);
      setRows((prev) => prev.filter((a) => a.id !== agency.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Geri getirilemedi");
    } finally {
      setRestoringId(null);
    }
  };

  const totalCount = rows.length;
  const totalRes = rows.reduce((s, a) => s + (a.reservation_count || 0), 0);

  return (
    <div className="space-y-5" data-testid="acente-deleted-agencies-page">
      <div>
        <Link to="/acente/acenteler" className="text-sm text-brand-navy/60 hover:text-brand-orange inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Acentelere Dön
        </Link>
        <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-rose-600 font-semibold">Yönetici · Arşiv</div>
            <h1 className="font-serif text-3xl text-brand-navy mt-1">Silinen Şirketler</h1>
            <p className="text-xs text-slate-500 mt-1">
              Silinen acenteler ve rezervasyonları burada saklanır. Kalıcı silme yoktur —
              her satır tek tıkla geri getirilebilir.
            </p>
          </div>
          <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-900 flex items-center gap-4" data-testid="deleted-agencies-stats">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center">
                <Archive size={16} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-rose-700 font-semibold">Şirket</div>
                <div className="font-serif text-2xl leading-none mt-0.5" data-testid="deleted-agencies-count">{totalCount}</div>
              </div>
            </div>
            <div className="border-l border-rose-200 pl-3">
              <div className="text-[10px] uppercase tracking-widest text-rose-700 font-semibold">Rezervasyon</div>
              <div className="font-serif text-2xl leading-none mt-0.5" data-testid="deleted-agencies-res-count">{totalRes}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-brand-navy/10 shadow-sm">
        <div className="p-4 border-b border-slate-100">
          <Input
            placeholder="Şirket adı / e-posta / silen kişi ara..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-md"
            data-testid="deleted-agencies-search"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="deleted-agencies-table">
            <thead className="text-xs uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
              <tr className="text-left">
                <th className="py-2 pl-3 pr-1 w-8"></th>
                <th className="py-2 px-2">Şirket</th>
                <th className="py-2 px-2">İletişim</th>
                <th className="py-2 px-2">Rezervasyon</th>
                <th className="py-2 px-2">Silinme</th>
                <th className="py-2 px-2 w-40 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="py-6 text-center text-slate-500">Yükleniyor...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-slate-500">
                    <div className="inline-flex flex-col items-center gap-2">
                      <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-500 flex items-center justify-center">
                        <Archive size={22} />
                      </div>
                      <div className="text-sm">Arşiv boş.</div>
                      <div className="text-xs text-slate-400">Silinen şirketler ve rezervasyonları burada listelenir.</div>
                    </div>
                  </td>
                </tr>
              )}
              {filtered.map((a) => {
                const exp = expanded[a.id];
                const isOpen = !!exp?.open;
                const rez = exp?.reservations || [];
                return (
                  <React.Fragment key={a.id}>
                    <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`deleted-agency-row-${a.id}`}>
                      <td className="py-3 pl-3 pr-1 align-top">
                        <button
                          onClick={() => toggleRow(a)}
                          className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition"
                          data-testid={`deleted-agency-expand-${a.id}`}
                          title={isOpen ? "Kapat" : "Rezervasyonları göster"}
                        >
                          {isOpen ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                        </button>
                      </td>
                      <td className="py-3 px-2 align-top">
                        <div className="flex items-start gap-2">
                          <Building2 size={14} className="text-rose-600 mt-0.5"/>
                          <div>
                            <div className="font-medium text-brand-navy" data-testid={`deleted-agency-name-${a.id}`}>
                              {a.name}
                            </div>
                            {a.tax_id && <div className="text-[11px] text-slate-500 font-mono mt-0.5">VN: {a.tax_id}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-xs align-top text-slate-700">
                        {a.email && <div>{a.email}</div>}
                        {a.phone && <div className="text-slate-500 mt-0.5">{a.phone}</div>}
                        {!a.email && !a.phone && <span className="text-slate-400">—</span>}
                      </td>
                      <td className="py-3 px-2 align-top">
                        <span
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-rose-100 text-rose-800 border border-rose-200"
                          data-testid={`deleted-agency-res-count-${a.id}`}
                        >
                          <Receipt size={11}/> {a.reservation_count} rezervasyon
                        </span>
                      </td>
                      <td className="py-3 px-2 text-xs align-top text-slate-600">
                        <div data-testid={`deleted-agency-when-${a.id}`}>{formatTr(a.deleted_at?.slice(0, 10) || "")}</div>
                        {a.deleted_by_name && <div className="text-slate-500 mt-0.5">Silen: {a.deleted_by_name}</div>}
                      </td>
                      <td className="py-3 px-2 text-right align-top">
                        <button
                          onClick={() => restore(a)}
                          disabled={restoringId === a.id}
                          className="text-xs px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1 disabled:opacity-70"
                          data-testid={`deleted-agency-restore-${a.id}`}
                        >
                          <Undo2 size={12}/> {restoringId === a.id ? "Getiriliyor..." : "Geri Getir"}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr data-testid={`deleted-agency-drilldown-${a.id}`}>
                        <td></td>
                        <td colSpan={5} className="pb-4">
                          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
                              {a.name} · Arşiv Rezervasyonları
                            </div>
                            {exp?.loading ? (
                              <div className="text-xs text-slate-500 py-4 text-center">Yükleniyor...</div>
                            ) : rez.length === 0 ? (
                              <div className="text-xs text-slate-500 py-4 text-center">Bu şirkete ait arşiv rezervasyon bulunamadı.</div>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-300">
                                    <tr className="text-left">
                                      <th className="py-1.5 px-2">Numara</th>
                                      <th className="py-1.5 px-2">Grup / Müşteri</th>
                                      <th className="py-1.5 px-2">Otel</th>
                                      <th className="py-1.5 px-2">Giriş</th>
                                      <th className="py-1.5 px-2">Kişi</th>
                                      <th className="py-1.5 px-2">Oda</th>
                                      <th className="py-1.5 px-2 text-right">Anlaşılan (SAR)</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rez.map((r) => {
                                      const heads = (r.adults||0)+(r.children||0)+(r.staff||0);
                                      const rooms = ["r1","r2","r3","r4","r5"].reduce((s,k)=>s+(r.rooms?.[k]||0),0);
                                      const total = (r.agreed_room_price||0)+(r.agreed_meal_price||0);
                                      return (
                                        <tr key={r.id} className="border-b border-slate-200 last:border-none" data-testid={`deleted-agency-rez-${r.id}`}>
                                          <td className="py-1.5 px-2 font-mono">{r.reservation_number}</td>
                                          <td className="py-1.5 px-2">
                                            {r.group_name || <span className="text-slate-400 italic">—</span>}
                                            {r.customer_phone && <div className="text-[10px] text-slate-500">📞 {r.customer_phone}</div>}
                                          </td>
                                          <td className="py-1.5 px-2">{r.hotel_name || "—"}</td>
                                          <td className="py-1.5 px-2 inline-flex items-center gap-1"><Calendar size={9}/> {formatTr(r.checkin)}</td>
                                          <td className="py-1.5 px-2 inline-flex items-center gap-1"><Users size={9}/> {heads}</td>
                                          <td className="py-1.5 px-2 inline-flex items-center gap-1"><BedDouble size={9}/> {rooms}</td>
                                          <td className="py-1.5 px-2 text-right font-mono">{total.toLocaleString("tr-TR")}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
